import { PVQResponseSection } from 'node-api/assessments/AssessmentsApi.types';
import { getAnswerValue, getAnswersBySection } from 'node-api/health-records/HealthRecords-utils';

/**
 * Serializes PVQ (Pre-Visit Questionnaire) content into section-grouped Markdown
 * so it can be copied to the clipboard and pasted as input to other agents.
 *
 * Output contains questions & answers only — no patient identifiers or metadata.
 * Two source formats are supported: the legacy `PVQResponseSection[]` and the
 * newer Typeform-derived sections produced by `getAnswersBySection`.
 */

const DOCUMENT_HEADER = '# Pre-Visit Questionnaire';
const EMPTY_ANSWER = '_(no answer)_';

/** Trim and normalize CRLF so multi-line answers don't break Markdown structure. */
const cleanText = (value?: string): string => (value ?? '').replace(/\r\n?/g, '\n').trim();

/** Returns the cleaned answer, or an explicit placeholder when it is empty. */
const answerLine = (value?: string): string => {
  const cleaned = cleanText(value);
  return cleaned.length > 0 ? cleaned : EMPTY_ANSWER;
};

const sectionHeader = (name: string): string => `## ${cleanText(name)}`;

const questionTitle = (title: string): string => `**${cleanText(title)}**`;

/** Joins non-empty section blocks under the document header with a trailing newline. */
const assembleDocument = (sectionBlocks: string[]): string => {
  const blocks = sectionBlocks.filter((block) => block.length > 0);

  if (blocks.length === 0) {
    return '';
  }

  return `${[DOCUMENT_HEADER, ...blocks].join('\n\n')}\n`;
};

/** Legacy format: sections of fields, where a field may carry nested sub-questions. */
export const legacyPvqToMarkdown = (sections: PVQResponseSection[]): string => {
  const sectionBlocks = sections.map((section) => {
    const pairs = section.fields.map((field) => {
      const lines = [questionTitle(field.title)];
      const hasOwnAnswer = cleanText(field.answer).length > 0;
      const hasSubQuestions = !!field.fields && field.fields.length > 0;

      if (hasOwnAnswer) {
        lines.push(answerLine(field.answer));
      }

      if (hasSubQuestions) {
        field.fields?.forEach((subField) => {
          lines.push(`- **${cleanText(subField.title)}**`);
          lines.push(`  ${answerLine(subField.answer)}`);
        });
      } else if (!hasOwnAnswer) {
        lines.push(EMPTY_ANSWER);
      }

      return lines.join('\n');
    });

    if (pairs.length === 0) {
      return '';
    }

    return [sectionHeader(section.title), ...pairs].join('\n\n');
  });

  return assembleDocument(sectionBlocks);
};

/** New format: Typeform answers already grouped into sections of flat questions. */
export const formAnswersToMarkdown = (sections: ReturnType<typeof getAnswersBySection>): string => {
  const sectionBlocks = sections.map((section) => {
    const pairs = section.questions.map((question) =>
      [questionTitle(question.field.title), answerLine(getAnswerValue(question.answer))].join('\n')
    );

    if (pairs.length === 0) {
      return '';
    }

    return [sectionHeader(section.name), ...pairs].join('\n\n');
  });

  return assembleDocument(sectionBlocks);
};
